import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Box, LinearProgress } from '@mui/material';
import '../App.css';
import PageLoader from '../pageLoader';
import DqTodaySection from './dqTodaySection';
import DqOverviewSection from './dqOverviewSection';
import DqChartsSection from './dqChartsSection';
import DqUsersTable from './dqUsersTable';
import {
  NERVE_BLOCKS_APP_ID,
  PAGE_SIZE_OPTIONS,
  computePresetDates,
  downloadCsv,
  exportTimestamp,
  formatDisplayDate,
  formatDateTime,
  formatLocalDate,
  getApiBaseUrl,
} from './dqUtils';

const SORTABLE_COLUMNS = new Set([
  'lastAnswerDate',
  'firstAnswerDate',
  'currentStreak',
  'accuracy',
  'totalQuestionsAnswered',
  'totalCorrectAnswers',
  'leaderboardRank',
  'subscriptionType',
  'firstPurchaseDate',
  'subscribedAfterFirstAnswer',
  'daysToFirstPurchase',
]);

const EMPTY_FILTERS = {
  name: '',
  email: '',
  firstAnswerDate: '',
  lastAnswerDate: '',
  currentStreak: '',
  accuracy: '',
  totalQuestionsAnswered: '',
  totalCorrectAnswers: '',
  leaderboardRank: '',
  subscriptionType: '',
  firstPurchaseDate: '',
  subscribedAfterFirstAnswer: '',
};

const GamificationDashboard = ({ setLoading, loading, selectedApp }) => {
  const initial = computePresetDates('7days');

  const [dateRange, setDateRange] = useState(initial.dateRange);
  const [startDate, setStartDate] = useState(initial.startDate);
  const [endDate, setEndDate] = useState(initial.endDate);
  const [subscription, setSubscription] = useState('all');

  const [dashboardData, setDashboardData] = useState(null);
  const [answerTrends, setAnswerTrends] = useState([]);
  const [questions, setQuestions] = useState([]);
  const [chartTab, setChartTab] = useState(0);

  const [users, setUsers] = useState([]);
  const [usersTotal, setUsersTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [filters, setFilters] = useState(EMPTY_FILTERS);
  const [debouncedFilters, setDebouncedFilters] = useState(EMPTY_FILTERS);
  const [sort, setSort] = useState('leaderboardRank');
  const [order, setOrder] = useState('asc');

  const [chartsLoading, setChartsLoading] = useState(false);
  const [tableLoading, setTableLoading] = useState(false);
  const [refreshing, setRefreshing] = useState(false);

  const dashboardFetchId = useRef(0);
  const chartsFetchId = useRef(0);
  const usersFetchId = useRef(0);
  const isFirstLoadRef = useRef(true);

  const buildChartParams = useCallback(() => {
    const params = new URLSearchParams();
    params.set('app_id', selectedApp);
    params.set('start_date', formatLocalDate(startDate));
    params.set('end_date', formatLocalDate(endDate));
    params.set('subscription', subscription);
    return params;
  }, [selectedApp, startDate, endDate, subscription]);

  const buildDashboardParams = useCallback(() => {
    const params = new URLSearchParams();
    params.set('app_id', selectedApp);
    const preset = computePresetDates('7days');
    params.set('start_date', formatLocalDate(preset.startDate));
    params.set('end_date', formatLocalDate(preset.endDate));
    params.set('subscription', 'all');
    return params;
  }, [selectedApp]);

  const handleDateRangeChange = (range) => {
    if (range === 'custom') {
      setDateRange('custom');
      return;
    }
    const preset = computePresetDates(range);
    setDateRange(preset.dateRange);
    setStartDate(preset.startDate);
    setEndDate(preset.endDate);
  };

  const handleStartDateChange = (date) => {
    if (!date) return;
    setDateRange('custom');
    setStartDate(date);
  };

  const handleEndDateChange = (date) => {
    if (!date) return;
    setDateRange('custom');
    setEndDate(date);
  };

  const handleSearchChange = (key, value) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
    setPage(0);
  };

  const buildUsersQueryParams = useCallback(
    (exportAll = false) => {
      const params = new URLSearchParams();
      params.set('app_id', selectedApp);
      params.set('page', String(page + 1));
      params.set('limit', String(rowsPerPage));
      params.set('sort', sort);
      params.set('order', order);
      Object.entries(debouncedFilters).forEach(([key, value]) => {
        if (value != null && String(value).trim() !== '') {
          params.set(key, String(value));
        }
      });
      if (exportAll) {
        params.set('export', '1');
      }
      return params;
    },
    [debouncedFilters, order, page, rowsPerPage, selectedApp, sort]
  );

  const handleSortChange = (column) => {
    if (!SORTABLE_COLUMNS.has(column)) return;
    if (sort === column) {
      setOrder((prev) => (prev === 'asc' ? 'desc' : 'asc'));
    } else {
      setSort(column);
      setOrder(column === 'leaderboardRank' ? 'asc' : 'desc');
    }
    setPage(0);
  };

  const fetchDashboard = useCallback(async () => {
    const baseUrl = getApiBaseUrl();
    if (!baseUrl || selectedApp !== NERVE_BLOCKS_APP_ID) return;

    const fetchId = ++dashboardFetchId.current;
    const isInitial = isFirstLoadRef.current;

    if (isInitial) {
      setLoading(true);
    } else {
      setRefreshing(true);
    }

    try {
      const res = await fetch(`${baseUrl}/daily-questions-dashboard?${buildDashboardParams()}`);
      if (!res.ok) {
        throw new Error(`Dashboard request failed: ${res.status}`);
      }
      const data = await res.json();
      if (fetchId !== dashboardFetchId.current) return;
      setDashboardData(data);
    } catch (err) {
      console.error('Failed to fetch DQ dashboard:', err);
      if (process.env.REACT_APP_ENABLE_ERROR_ALERTS === 'true') {
        alert('Error loading Daily Questions dashboard');
      }
    } finally {
      if (fetchId === dashboardFetchId.current) {
        if (isInitial) {
          isFirstLoadRef.current = false;
          setLoading(false);
        }
        setRefreshing(false);
      }
    }
  }, [buildDashboardParams, selectedApp, setLoading]);

  const fetchCharts = useCallback(async () => {
    const baseUrl = getApiBaseUrl();
    if (!baseUrl || selectedApp !== NERVE_BLOCKS_APP_ID) return;

    const fetchId = ++chartsFetchId.current;
    setChartsLoading(true);

    try {
      const query = buildChartParams().toString();
      const [trendsRes, questionsRes] = await Promise.all([
        fetch(`${baseUrl}/daily-questions/answer-trends?${query}`),
        fetch(`${baseUrl}/daily-questions/question-performance?${query}`),
      ]);

      if (fetchId !== chartsFetchId.current) return;

      if (trendsRes.ok) {
        const trendsData = await trendsRes.json();
        setAnswerTrends(Array.isArray(trendsData.answerTrends) ? trendsData.answerTrends : []);
      } else {
        setAnswerTrends([]);
      }

      if (questionsRes.ok) {
        const questionsData = await questionsRes.json();
        setQuestions(Array.isArray(questionsData.questions) ? questionsData.questions : []);
      } else {
        setQuestions([]);
      }
    } catch (err) {
      console.error('Failed to fetch DQ charts:', err);
      if (fetchId === chartsFetchId.current) {
        setAnswerTrends([]);
        setQuestions([]);
      }
    } finally {
      if (fetchId === chartsFetchId.current) {
        setChartsLoading(false);
      }
    }
  }, [buildChartParams, selectedApp]);

  const fetchUsers = useCallback(
    async (exportAll = false) => {
      const baseUrl = getApiBaseUrl();
      if (!baseUrl || selectedApp !== NERVE_BLOCKS_APP_ID) return null;

      const fetchId = ++usersFetchId.current;
      if (!exportAll) {
        setTableLoading(true);
      }

      const params = buildUsersQueryParams(exportAll);

      try {
        const res = await fetch(`${baseUrl}/daily-questions/users?${params}`);
        if (!res.ok) {
          throw new Error(`Users request failed: ${res.status}`);
        }
        const data = await res.json();
        if (fetchId !== usersFetchId.current) return null;

        if (!exportAll) {
          setUsers(Array.isArray(data.users) ? data.users : []);
          setUsersTotal(typeof data.total === 'number' ? data.total : 0);
        }
        return data;
      } catch (err) {
        console.error('Failed to fetch DQ users:', err);
        if (process.env.REACT_APP_ENABLE_ERROR_ALERTS === 'true' && !exportAll) {
          alert('Error loading Daily Questions users');
        }
        return null;
      } finally {
        if (fetchId === usersFetchId.current && !exportAll) {
          setTableLoading(false);
        }
      }
    },
    [buildUsersQueryParams, selectedApp]
  );

  const exportUsers = async () => {
    setTableLoading(true);
    try {
      const data = await fetchUsers(true);
      const rows = Array.isArray(data?.users) ? data.users : [];
      downloadCsv(
        rows.map((row) => ({
          name: row.name || '',
          email: row.email || '',
          firstAnswerDate: formatDisplayDate(row.firstAnswerDate),
          lastAnswerDate: formatDisplayDate(row.lastAnswerDate),
          currentStreak: row.currentStreak ?? 0,
          totalQuestionsAnswered: row.totalQuestionsAnswered ?? 0,
          totalCorrectAnswers: row.totalCorrectAnswers ?? 0,
          accuracy: row.accuracy ?? 0,
          leaderboardRank: row.leaderboardRank ?? '',
          subscriptionType: row.subscriptionType || '',
          firstPurchaseDate: formatDateTime(row.firstPurchaseDate),
          subscribedAfterFirstAnswer: row.subscribedAfterFirstAnswer === true ? 'True' : 'False',
          daysToFirstPurchase:
            row.subscribedAfterFirstAnswer === true && row.daysToFirstPurchase != null
              ? row.daysToFirstPurchase
              : '',
        })),
        `dq_users_${exportTimestamp()}.csv`
      );
    } finally {
      setTableLoading(false);
    }
  };

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedFilters(filters), 400);
    return () => clearTimeout(timer);
  }, [filters]);

  useEffect(() => {
    setPage(0);
  }, [selectedApp, debouncedFilters]);

  useEffect(() => {
    if (selectedApp !== NERVE_BLOCKS_APP_ID) return;
    isFirstLoadRef.current = true;
    setDashboardData(null);
    setAnswerTrends([]);
    setQuestions([]);
    setUsers([]);
    setUsersTotal(0);
    setPage(0);
    setFilters(EMPTY_FILTERS);
    setDebouncedFilters(EMPTY_FILTERS);
  }, [selectedApp]);

  useEffect(() => {
    if (!selectedApp || selectedApp !== NERVE_BLOCKS_APP_ID) return;
    fetchDashboard();
  }, [selectedApp, fetchDashboard]);

  useEffect(() => {
    if (!selectedApp || selectedApp !== NERVE_BLOCKS_APP_ID || !startDate || !endDate) return;
    const timer = setTimeout(() => {
      fetchCharts();
    }, 150);
    return () => clearTimeout(timer);
  }, [selectedApp, startDate, endDate, subscription, fetchCharts]);

  useEffect(() => {
    if (!selectedApp || selectedApp !== NERVE_BLOCKS_APP_ID) return;
    fetchUsers();
  }, [selectedApp, page, rowsPerPage, debouncedFilters, sort, order, fetchUsers]);

  const showInitialLoader = loading && !dashboardData;

  if (showInitialLoader) {
    return <PageLoader />;
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', width: '100%', minWidth: 0, boxSizing: 'border-box' }}>
      {refreshing ? (
        <LinearProgress sx={{ position: 'fixed', top: 0, left: 0, right: 0, zIndex: 1300 }} />
      ) : null}

      <DqTodaySection today={dashboardData?.today} />
      <DqOverviewSection overview={dashboardData?.overview} />

      <DqChartsSection
        chartTab={chartTab}
        onChartTabChange={setChartTab}
        answerTrends={answerTrends}
        questions={questions}
        chartsLoading={chartsLoading}
        dateRange={dateRange}
        startDate={startDate}
        endDate={endDate}
        subscription={subscription}
        onDateRangeChange={handleDateRangeChange}
        onStartDateChange={handleStartDateChange}
        onEndDateChange={handleEndDateChange}
        onSubscriptionChange={setSubscription}
      />

      <DqUsersTable
        users={users}
        total={usersTotal}
        page={page}
        rowsPerPage={rowsPerPage}
        pageSizeOptions={PAGE_SIZE_OPTIONS}
        filters={filters}
        sort={sort}
        order={order}
        tableLoading={tableLoading}
        onSearchChange={handleSearchChange}
        onSortChange={handleSortChange}
        onPageChange={setPage}
        onRowsPerPageChange={(value) => {
          setRowsPerPage(value);
          setPage(0);
        }}
        onExport={exportUsers}
      />
    </Box>
  );
};

export default GamificationDashboard;
